import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  KeyRound,
  Phone,
  Send,
  Shield,
  ShieldCheck,
  ShieldOff,
  Star,
  Truck,
  UserCog,
} from 'lucide-react';
import type { JobStatus, UserRole, VehicleRequired, Urgency } from '@godrop/types';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { StatusChip } from '../components/ui/status-chip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../lib/toast';
import { fmtDate, fmtMoney, fmtRelative } from '../lib/utils';
import { StatCard } from '../components/StatCard';

interface AdminUser {
  id: string;
  phone: string;
  name: string | null;
  role: UserRole;
  vehicle_mode: string | null;
  profile_image_url: string | null;
  telegram_linked: boolean;
  telegram_username: string | null;
  telegram_chat_id: string | null;
  web_push_enabled: boolean;
  is_available: boolean;
  is_suspended: boolean;
  reliability_score: number;
  completed_count: number;
  cancellation_count: number;
  no_show_count: number;
  average_rating: number | null;
  contact_method: string;
  current_lat: number | null;
  current_lng: number | null;
  last_location_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UserJob {
  id: string;
  code: string;
  title: string;
  status: JobStatus;
  vehicle_required: VehicleRequired;
  urgency: Urgency;
  payout: number;
  pickup_area: string;
  dropoff_area: string;
  pod_uploaded_at: string | null;
  created_at: string;
  accepted_at: string | null;
}

interface ReceivedRating {
  id: string;
  job_id: string;
  rater_id: string;
  ratee_id: string;
  role_of_ratee: 'poster' | 'courier';
  stars: number;
  comment: string | null;
  created_at: string;
  job_code: string;
  job_title: string;
  rater_phone: string;
  rater_name: string | null;
}

const ROLES: UserRole[] = ['business', 'courier', 'both', 'admin'];

function initials(s: string | null | undefined): string {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/).filter(Boolean);
  return parts.length === 1
    ? parts[0]!.slice(0, 2).toUpperCase()
    : (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [posterJobs, setPosterJobs] = useState<UserJob[] | null>(null);
  const [courierJobs, setCourierJobs] = useState<UserJob[] | null>(null);
  const [ratings, setRatings] = useState<ReceivedRating[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function load() {
    if (!id) return;
    setUser(null);
    Promise.all([
      api<AdminUser>(`/api/admin/users/${id}`),
      api<{ jobs: UserJob[] }>(`/api/admin/users/${id}/jobs?role=poster`),
      api<{ jobs: UserJob[] }>(`/api/admin/users/${id}/jobs?role=courier`),
      api<{ ratings: ReceivedRating[] }>(`/api/admin/users/${id}/ratings`),
    ])
      .then(([u, p, c, r]) => {
        setUser(u);
        setPosterJobs(p.jobs);
        setCourierJobs(c.jobs);
        setRatings(r.ratings);
      })
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }

  useEffect(load, [id]);

  async function suspend(toggle: boolean) {
    if (!id) return;
    try {
      await api(`/api/admin/users/${id}/suspend`, { method: 'POST', json: { suspend: toggle } });
      toast.show(toggle ? 'User suspended' : 'User reinstated', 'success');
      load();
    } catch (e) {
      toast.show(e instanceof ApiError ? `Failed (${e.status})` : 'Failed', 'error');
    }
  }

  async function setRole(role: UserRole) {
    if (!id) return;
    try {
      await api(`/api/admin/users/${id}/role`, { method: 'POST', json: { role } });
      toast.show(`Role set to ${role}`, 'success');
      load();
    } catch (e) {
      toast.show(e instanceof ApiError ? `Failed (${e.status})` : 'Failed', 'error');
    }
  }

  async function setPhone(phone: string) {
    if (!id) return;
    try {
      await api(`/api/admin/users/${id}/phone`, { method: 'POST', json: { phone } });
      toast.show('Phone updated', 'success');
      load();
    } catch (e) {
      const message =
        e instanceof ApiError && e.status === 409
          ? 'That phone is already in use.'
          : e instanceof ApiError && e.status === 400
            ? 'Phone must be +65 8/9 + 7 digits.'
            : 'Update failed';
      toast.show(message, 'error');
      throw e;
    }
  }

  if (!id) return null;
  if (err) {
    return (
      <Shell title="User">
        <Card className="border-error/40 bg-error-tint">
          <CardContent className="py-4 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      </Shell>
    );
  }
  if (!user) {
    return (
      <Shell title="User">
        <div className="space-y-3">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell title={user.name ?? user.phone}>
      <div className="mb-4">
        <Button asChild variant="ghost" size="sm" className="-ml-3 h-7 px-2 text-text-muted">
          <Link to="/users">
            <ChevronLeft className="h-4 w-4" /> All users
          </Link>
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="flex flex-col items-start gap-4 p-5 md:flex-row md:items-center">
          <Avatar className="h-16 w-16">
            {user.profile_image_url && <AvatarImage src={user.profile_image_url} alt="" />}
            <AvatarFallback className="text-base">{initials(user.name ?? user.phone)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-2xl font-bold tracking-tight text-text">
                {user.name ?? '—'}
              </h2>
              <Badge variant={user.role === 'admin' ? 'brand' : 'default'}>{user.role}</Badge>
              {user.vehicle_mode && <Badge>{user.vehicle_mode}</Badge>}
              {user.is_suspended && <Badge variant="urgent">suspended</Badge>}
              {!user.is_available && !user.is_suspended && (
                <Badge>off-duty</Badge>
              )}
            </div>
            <p className="tnum mt-1 text-sm text-text-muted">{user.phone}</p>
            <p className="mt-1 text-xs text-text-muted">
              Joined {fmtDate(user.created_at)} · Updated {fmtRelative(user.updated_at)}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ChangePhoneDialog currentPhone={user.phone} onSubmit={setPhone} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm">
                  <UserCog className="h-4 w-4" /> Change role
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Set role</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ROLES.map((r) => (
                  <DropdownMenuItem
                    key={r}
                    onClick={() => setRole(r)}
                    disabled={r === user.role}
                  >
                    {r}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {user.role !== 'admin' && (
              <ConfirmDialog
                title="Make this user an admin?"
                description="They get full access to the admin dashboard — every user, drop, rating, and announcement. This replaces their current role."
                confirmLabel="Make admin"
                variant="primary"
                onConfirm={() => setRole('admin')}
                trigger={
                  <Button variant="secondary" size="sm">
                    <ShieldCheck className="h-4 w-4" /> Make admin
                  </Button>
                }
              />
            )}
            <ConfirmDialog
              title={user.is_suspended ? 'Reinstate this user?' : 'Suspend this user?'}
              description={
                user.is_suspended
                  ? 'They will be able to sign in and post or accept drops again.'
                  : 'They will be locked out of login. Existing accepted drops are unaffected.'
              }
              confirmLabel={user.is_suspended ? 'Reinstate' : 'Suspend'}
              variant={user.is_suspended ? 'primary' : 'danger'}
              onConfirm={() => suspend(!user.is_suspended)}
              trigger={
                <Button
                  variant={user.is_suspended ? 'primary' : 'danger'}
                  size="sm"
                >
                  {user.is_suspended ? <Shield className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                  {user.is_suspended ? 'Reinstate' : 'Suspend'}
                </Button>
              }
            />
          </div>
        </CardContent>
      </Card>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Completed"
          value={user.completed_count}
          icon={Truck}
          accent="completed"
        />
        <StatCard
          label="Cancelled"
          value={user.cancellation_count}
          icon={ShieldOff}
          accent="cancelled"
        />
        <StatCard
          label="Reliability"
          value={user.reliability_score.toFixed(1)}
          icon={Star}
          accent="brand"
        />
        <StatCard
          label="Avg rating"
          value={user.average_rating != null ? user.average_rating.toFixed(2) : '—'}
          icon={Star}
          accent="open"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Activity</CardTitle>
            <CardDescription>Drops as poster, drops as courier, ratings received</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="poster">
              <TabsList>
                <TabsTrigger value="poster">As poster ({posterJobs?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="courier">As courier ({courierJobs?.length ?? 0})</TabsTrigger>
                <TabsTrigger value="ratings">Ratings ({ratings?.length ?? 0})</TabsTrigger>
              </TabsList>
              <TabsContent value="poster">
                <JobsMiniTable jobs={posterJobs} />
              </TabsContent>
              <TabsContent value="courier">
                <JobsMiniTable jobs={courierJobs} />
              </TabsContent>
              <TabsContent value="ratings">
                <RatingsMiniList ratings={ratings} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Channels</CardTitle>
              <CardDescription>How GoDrop reaches them</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Row
                icon={<Send className="h-4 w-4" />}
                label="Telegram"
                value={
                  user.telegram_linked
                    ? user.telegram_username
                      ? `@${user.telegram_username}`
                      : `chat ${user.telegram_chat_id}`
                    : 'Not linked'
                }
                ok={user.telegram_linked}
              />
              <Row
                icon={<Phone className="h-4 w-4" />}
                label="Web push"
                value={user.web_push_enabled ? 'Subscribed' : 'Not subscribed'}
                ok={user.web_push_enabled}
              />
              <Row
                icon={<KeyRound className="h-4 w-4" />}
                label="Contact method"
                value={user.contact_method}
                ok
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Last known location</CardTitle>
              <CardDescription>Used for ping eligibility (30-min staleness)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {user.current_lat != null && user.current_lng != null ? (
                <>
                  <p className="tnum text-text">
                    {user.current_lat.toFixed(5)}, {user.current_lng.toFixed(5)}
                  </p>
                  <p className="text-xs text-text-muted">
                    Updated {fmtRelative(user.last_location_updated_at)}
                  </p>
                  <Button asChild variant="ghost" size="sm" className="-ml-3">
                    <a
                      href={`https://www.google.com/maps?q=${user.current_lat},${user.current_lng}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Maps
                    </a>
                  </Button>
                </>
              ) : (
                <EmptyState title="No location reported." />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Identifiers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <KeyVal label="ID" value={user.id} />
              {user.telegram_chat_id && (
                <KeyVal label="TG chat" value={user.telegram_chat_id} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Shell>
  );
}

function Row({
  icon,
  label,
  value,
  ok,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  ok?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-md ${
          ok ? 'bg-status-completed-tint text-status-completed-on-tint' : 'bg-surface-muted text-text-muted'
        }`}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</p>
        <p className="truncate text-sm text-text">{value}</p>
      </div>
    </div>
  );
}

function KeyVal({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">{label}</p>
      <p className="font-mono text-text-subtle break-all">{value}</p>
    </div>
  );
}

function JobsMiniTable({ jobs }: { jobs: UserJob[] | null }) {
  if (!jobs) return <Skeleton className="h-32 w-full" />;
  if (jobs.length === 0) return <EmptyState title="No drops in this view." />;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Code</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Route</TableHead>
          <TableHead className="text-right tnum">Payout</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">When</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {jobs.map((j) => (
          <TableRow key={j.id}>
            <TableCell className="tnum text-xs font-semibold text-text-subtle">
              <Link to={`/jobs/${j.id}`}>{j.code}</Link>
            </TableCell>
            <TableCell className="font-medium">
              <Link to={`/jobs/${j.id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                {j.title}
              </Link>
            </TableCell>
            <TableCell className="text-xs text-text-subtle">
              {j.pickup_area} → {j.dropoff_area}
            </TableCell>
            <TableCell className="text-right tnum text-brand-700 dark:text-brand-500">
              {fmtMoney(j.payout)}
            </TableCell>
            <TableCell>
              <StatusChip status={j.status} />
            </TableCell>
            <TableCell className="text-right text-xs text-text-muted">
              {fmtRelative(j.accepted_at ?? j.created_at)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RatingsMiniList({ ratings }: { ratings: ReceivedRating[] | null }) {
  if (!ratings) return <Skeleton className="h-32 w-full" />;
  if (ratings.length === 0) return <EmptyState title="No ratings received." />;
  return (
    <ul className="divide-y divide-border">
      {ratings.map((r) => (
        <li key={r.id} className="py-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="tnum text-amber-600 dark:text-amber-400">
              {'★'.repeat(r.stars)}
              <span className="text-text-muted">{'★'.repeat(5 - r.stars)}</span>
            </span>
            <Badge>as {r.role_of_ratee}</Badge>
            <span className="ml-auto text-xs text-text-muted">{fmtRelative(r.created_at)}</span>
          </div>
          {r.comment && <p className="mt-1 text-text-subtle">"{r.comment}"</p>}
          <p className="mt-1 text-xs text-text-muted">
            <Link to={`/jobs/${r.job_id}`} className="tnum hover:text-brand-700 dark:hover:text-brand-500">
              {r.job_code}
            </Link>{' '}
            · from {r.rater_name ?? r.rater_phone}
          </p>
        </li>
      ))}
    </ul>
  );
}

function ChangePhoneDialog({
  currentPhone,
  onSubmit,
}: {
  currentPhone: string;
  onSubmit: (phone: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState(currentPhone);

  async function go() {
    setBusy(true);
    try {
      await onSubmit(phone);
      setOpen(false);
    } catch {
      // toast handled upstream
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">
          <Phone className="h-4 w-4" /> Change phone
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Change phone number</DialogTitle>
          <DialogDescription>
            Lost-phone recovery — admin override. Must be a Singapore mobile.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="phone">New phone</Label>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value.trim())}
            placeholder="+6591234567"
            inputMode="tel"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={go} disabled={busy || phone === currentPhone}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

